import { Globe01Icon, Lock01Icon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { ResourceDetailLayout } from '@/components/ResourceDetailTabLayout/ResourceDetailTabLayout';
import {
  ResourceActionsSlot,
  ResourceTabsSlot,
} from '@/components/ResourceDetailTabLayout/ResourceTabLayoutPortals';
import { TabGroup } from '@/components/Tabs/Tabs';
import { useTab } from '@/components/Tabs/useTab';
import {
  Badge,
  TabLabel as TabLabelV2,
  TabList as TabListV2,
  TabPanel as TabPanelV2,
  TabPanels as TabPanelsV2,
} from '@/design-system';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { useOrganizationId, useSelectedTenant } from '@/hooks/useSwr';
import { ResourceType } from '@/types/schema';
import { addQueryParams } from '@/utils/add-query-params';
import { formatCount } from '@/utils/format-count';

import { hasProxyProvider } from '../Playground/utils/Playground.utils';
import { getPromptDisplayNameFromRepoObj } from '../Prompts/utils/Prompts.utils';
import { Commits } from './Commits';
import { OptimizationRunsPage } from './OptimizationRuns/OptimizationRunsPage';
import { PromptView } from './PromptView';
import ProxyProviderWarningModal from './ProxyProviderWarningModal';
import { RepoButtons } from './RepoHeader';
import { PromptPageTabs } from './constants';
import { useRepoData } from './hooks/useRepoData';
import { useRepoNavigation } from './hooks/useRepoNavigation';
import { getUpstreamRepoUrl } from './utils';

const RepoV2 = ({ inHub }: { inHub: boolean }) => {
  const { repoObj, commitObj, playgroundLinkBase, playgroundParams, loading } =
    useRepoData(inHub);
  const { owner, repo, commit } = useParams();

  const tab = useTab({
    defaultIndex: commit ? PromptPageTabs.COMMITS : PromptPageTabs.PROMPTS,
  });

  const { navigateToTab } = useRepoNavigation({
    owner,
    repo: repo!,
    inHub,
    latestCommitHash: repoObj?.repo.last_commit_hash?.slice(0, 8) ?? '',
  });

  const handleTabChange = (index: number) => {
    navigateToTab(index);
  };

  const isUsingProxyProvider = commitObj
    ? hasProxyProvider(commitObj.manifest)
    : false;

  const { data: tenant } = useSelectedTenant();
  const showProxyProviderWarning = useMemo(() => {
    const isPublic = repoObj?.repo.is_public;
    const isMyPrompt = repoObj?.repo.owner === tenant?.tenant_handle;
    return !!(isPublic && !isMyPrompt && isUsingProxyProvider);
  }, [
    repoObj?.repo.is_public,
    repoObj?.repo.owner,
    tenant?.tenant_handle,
    isUsingProxyProvider,
  ]);
  const repoDisplayName = repoObj?.repo
    ? getPromptDisplayNameFromRepoObj(repoObj?.repo)
    : '';

  const [showProxyProviderWarningModal, setShowProxyProviderWarningModal] =
    useState(false);

  const { value: promptOptimizationJobsEnabled } = useOrgConfig(
    OrgConfigs.prompt_optimization_jobs_enabled
  );
  const organizationId = useOrganizationId();

  const upstreamRepoUrl = getUpstreamRepoUrl({
    repoObj,
    currentOwner: owner,
    inHub,
    organizationId,
  });

  if (!repoObj) {
    return null;
  }

  return (
    <div className="flex flex-1 flex-col">
      <ResourceDetailLayout
        pageTitleProps={{
          title: repoDisplayName,
          type: ResourceType.Prompt,
          description: repoObj.repo.upstream_repo_full_name && (
            <>
              Forked from{' '}
              <Link
                to={addQueryParams(upstreamRepoUrl, { organizationId })}
                className="text-brand-primary"
              >
                {repoObj.repo.upstream_repo_full_name}
              </Link>
            </>
          ),
          rightDecorator: repoObj?.repo?.is_public ? (
            <Badge color="secondary" leftDecorator={Globe01Icon} size="sm">
              {`Public${repoObj?.repo?.is_archived ? ' Archive' : ''}`}
            </Badge>
          ) : (
            <Badge color="secondary" leftDecorator={Lock01Icon} size="sm">
              {`Private${repoObj?.repo?.is_archived ? ' Archive' : ''}`}
            </Badge>
          ),
        }}
        hideBreadcrumbs={true}
      >
        <TabGroup
          setTabMethod="merge"
          defaultIndex={tab}
          onChange={handleTabChange}
        >
          <ResourceTabsSlot.Fill>
            <TabListV2 className="mb-0">
              <TabLabelV2 label="Prompt" />
              <TabLabelV2
                label="Commits"
                badgeProps={
                  !loading && repoObj?.repo?.num_commits
                    ? {
                        children: formatCount(repoObj.repo.num_commits),
                        rounded: 'sm',
                      }
                    : undefined
                }
              />

              {!inHub && promptOptimizationJobsEnabled && (
                <TabLabelV2 label="Optimization Runs" />
              )}
            </TabListV2>
          </ResourceTabsSlot.Fill>
          <ResourceActionsSlot.Fill>
            <RepoButtons
              inHub={inHub}
              showProxyProviderWarning={showProxyProviderWarning}
              setShowProxyProviderWarningModal={
                setShowProxyProviderWarningModal
              }
            />
          </ResourceActionsSlot.Fill>
          <TabPanelsV2 className="flex min-h-[calc(100vh-170px)] flex-grow flex-col px-4">
            <TabPanelV2 className="-mx-4 flex flex-grow flex-col">
              {!loading && repoObj ? (
                <PromptView
                  inHub={inHub}
                  showProxyProviderWarning={showProxyProviderWarning}
                  setShowProxyProviderWarningModal={
                    setShowProxyProviderWarningModal
                  }
                  repoObj={repoObj}
                />
              ) : (
                <div className="p-4">
                  <LinearProgress />
                </div>
              )}
            </TabPanelV2>
            <TabPanelV2 className="-mx-4 h-[calc(100vh-155px)] overflow-hidden">
              <Commits
                inHub={inHub}
                showProxyProviderWarning={showProxyProviderWarning}
              />
            </TabPanelV2>
            {promptOptimizationJobsEnabled && (
              <TabPanelV2 className="">
                {repoObj?.repo.repo_handle ? (
                  <OptimizationRunsPage repoName={repoObj.repo.repo_handle} />
                ) : (
                  <ExpandableErrorAlert
                    error={new Error('There is no prompt')}
                    defaultOpen
                  />
                )}
              </TabPanelV2>
            )}
          </TabPanelsV2>
        </TabGroup>
      </ResourceDetailLayout>
      {showProxyProviderWarningModal && (
        <ProxyProviderWarningModal
          setShowProxyProviderWarningModal={setShowProxyProviderWarningModal}
          playgroundLinkBase={playgroundLinkBase}
          playgroundParams={playgroundParams}
        />
      )}
    </div>
  );
};

const Repo = ({ inHub }: { inHub: boolean }) => {
  return <RepoV2 inHub={inHub} />;
};

export default Repo;
